import videojs from "video.js";
import { VideoPreloaderWorker } from "../interfaces/PreloaderWorker";
import { PreloaderWorkerObserver } from "../interfaces/PreloaderWorkerObserver";
import { StreamData } from "../interfaces/StreamData";
import { QualityLevel } from "../../../model/qualityLevel/QualityLevel";
import { setAllowedQualityLevels } from "../../../utils/videoJsUtils";
import Representation = videojs.Representation;

export class VideoJsPreloaderWorker implements VideoPreloaderWorker {
  isRunning: boolean;
  isFinished: boolean = false;

  private readonly player: videojs.Player;
  private container: HTMLElement = VideoJsPreloaderWorker.getContainer();

  private lastTime: number;
  private observers: Set<PreloaderWorkerObserver> = new Set<PreloaderWorkerObserver>();

  constructor(
    readonly streamData: StreamData,
    readonly quality: QualityLevel,
  ) {
    this.player = this.createPlayer(streamData);
    this.player.on("canplaythrough", this.onBufferingFinished.bind(this));

    this.lastTime = 0;
    this.isRunning = false;

    this.setPlayerDimensions(window.innerWidth, window.innerHeight);
  }
  private static getContainer() {
    let container = document.getElementById("video-js-preloaders");

    if (container === null) {
      container = document.createElement("div");
      container.id = "video-js-preloaders";
      container.style.display = "none";

      document.body.append(container);
    }

    return container;
  }
  start() {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;

    this.player.src(this.streamData);

    this.player.currentTime(this.lastTime);
    this.player.volume(0);
    this.player.play().then(() => {
      const representation = setAllowedQualityLevels(this.player, this.quality);
      this.createMediaServiceManifestPreloaderElements(representation);
    });
  }

  pause() {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;
    this.lastTime = this.player.currentTime();

    this.player.reset();
    this.player.pause();
  }

  addObserver(observer: PreloaderWorkerObserver) {
    this.observers.add(observer);
  }

  removeObserver(observer: PreloaderWorkerObserver) {
    this.observers.delete(observer);
  }

  dispose() {
    this.pause();

    if (!this.player.isDisposed()) {
      this.player.dispose();
    }
  }

  protected setPlayerDimensions(width: number, height: number) {
    this.player.width(width);
    this.player.height(height);
  }

  protected onBufferingFinished() {
    if (this.isFinished) {
      return;
    }

    this.isFinished = true;

    // TODO make it check in a loop for buffer percetange?
    setTimeout(() => this.finish(), 1000);
  }

  protected trigger(event: keyof PreloaderWorkerObserver) {
    this.observers.forEach((observer) => observer[event]?.(this.streamData));
  }

  protected finish() {
    this.isRunning = false;

    this.trigger("onFinish");

    this.dispose();
  }

  protected createPlayer(source: StreamData): videojs.Player {
    const videoElement = document.createElement("video");
    this.container.append(videoElement);

    const player = videojs(videoElement);

    if (player.canPlayType(source.type) === "") {
      throw new Error("Video.js cannot play " + source.type);
    }

    return player;
  }

  protected createMediaServiceManifestPreloaderElements(representation: Representation): void {
    if (this.streamData.src.indexOf("filter=prebuffering-filter") !== -1) {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = this.streamData.src.replace(",filter=prebuffering-filter", "");
      this.container.appendChild(link);

      const videoLink = document.createElement("link");
      videoLink.rel = "preload";
      videoLink.as = "image";
      // tslint:disable:no-string-literal
      videoLink.href = this.streamData.src
        .replace("manifest(format=m3u8-aapl,audio-only=false,filter=prebuffering-filter)", representation["playlist"].uri)
        .replace(",filter=prebuffering-filter", "");
      // tslint:enable:no-string-literal

      this.container.appendChild(videoLink);
    }
  }
}
